/**
 * Workspace store — the single source of truth for the active workspace.
 *
 * Persists ``currentWorkspaceId`` to ``localStorage`` so a reload keeps the
 * user in the same business. On boot, the app shell:
 *   1. calls ``/api/v1/me`` → sets user id (frontend HTTP interceptor uses it)
 *   2. calls ``/api/v1/workspaces`` → hydrates ``workspaces``
 *   3. picks ``localStorage['cz.workspaceId']`` if it matches; else first.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { setUserId, setWorkspaceId } from "@/shared/api/http";
import type { Me, Workspace } from "@/entities/workspace/model";

interface WorkspaceStoreState {
  me: Me | null;
  workspaces: Workspace[];
  currentWorkspaceId: string | null;
  hydrated: boolean;

  setMe: (me: Me | null) => void;
  setWorkspaces: (list: Workspace[]) => void;
  selectWorkspace: (id: string | null) => void;
  markHydrated: () => void;
}

export const useWorkspaceStore = create<WorkspaceStoreState>()(
  persist(
    (set) => ({
      me: null,
      workspaces: [],
      currentWorkspaceId: null,
      hydrated: false,

      setMe: (me) => {
        setUserId(me?.id ?? null);
        set({ me });
      },
      setWorkspaces: (list) => set({ workspaces: list }),
      selectWorkspace: (id) => {
        setWorkspaceId(id);
        set({ currentWorkspaceId: id });
      },
      markHydrated: () => set({ hydrated: true }),
    }),
    {
      name: "cz.workspace-store",
      partialize: (state) => ({ currentWorkspaceId: state.currentWorkspaceId }),
    },
  ),
);

/** Selector helpers. */
export const selectCurrentWorkspace = (state: WorkspaceStoreState): Workspace | null => {
  if (!state.currentWorkspaceId) return null;
  return state.workspaces.find((w) => w.id === state.currentWorkspaceId) ?? null;
};
